import { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError } from "@bw/shared/api";

import { invitePlatformAdmin } from "@/shared/api/platformAdminAccess";
import {
  adminRosterAuthorityOutcome,
  normalizePlatformAdminEmail,
  refreshAdminRosterAuthority,
  refreshUserAdminAuthority,
  usePlatformAdminAccessAuthority,
  userAdminAuthorityOutcome,
  type PlatformAdminAccessRecovery,
  type PlatformAdminAccessWriteLease,
} from "@/shared/platformAdminAccessAuthority";
import { PLATFORM_ADMIN_ACCESS_MUTATION_KEY } from "@/shared/platformAdminAccessMutation";
import { isInternalPlatformEmail } from "@/shared/platformAccess";
import type { PlatformUserDetail } from "../api";

type UserAdminRecovery = Extract<
  PlatformAdminAccessRecovery,
  { surface: "user" }
>;

function userAdminRecoveryFor(
  recovery: PlatformAdminAccessRecovery | null | undefined,
  userId: string,
): UserAdminRecovery | null {
  return recovery?.surface === "user" && recovery.userId === userId
    ? recovery
    : null;
}

function describeAdminGrantError(error: unknown) {
  if (
    error instanceof ApiError &&
    error.message.includes("Google Workspace directory verification")
  ) {
    return "Internal employee verification is not configured in this environment yet.";
  }
  if (
    error instanceof ApiError &&
    error.message.includes("No Firebase user found")
  ) {
    return "This Google account has not signed into Badminton Warriors yet. Have them sign in once, then try again.";
  }
  if (
    error instanceof ApiError &&
    error.message.includes("Firebase admin service is not configured")
  ) {
    return "Google Workspace admin access is not configured in this environment yet.";
  }
  return "Failed to grant platform admin access.";
}

/**
 * Platform admin control aside — shows the user's platform-access state and,
 * for eligible internal-employee identities, the grant-admin action + dialog.
 * Owns the invite mutation (with the same user/users invalidations as before).
 */
export function UserPlatformAdminPanel({
  user,
  onGrantFeedbackChange,
}: {
  user: PlatformUserDetail;
  onGrantFeedbackChange?: (feedback: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const authority = usePlatformAdminAccessAuthority(queryClient);
  const privilegedWriteCount = useIsMutating({
    exact: true,
    mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
  });
  const [showGrantAdminDialog, setShowGrantAdminDialog] = useState(false);
  const [grantAdminError, setGrantAdminError] = useState<string | null>(null);
  const initialResolutionSequence = useRef(authority.resolution?.sequence ?? 0);
  const authorityRecovery = userAdminRecoveryFor(authority.recovery, user.uid);
  const routeResolution =
    authority.resolution &&
    authority.resolution.sequence > initialResolutionSequence.current
      ? authority.resolution
      : null;
  const authorityResolutionRecovery = userAdminRecoveryFor(
    routeResolution?.recovery,
    user.uid,
  );
  const authorityResolution =
    authorityResolutionRecovery && routeResolution ? routeResolution : null;
  const foreignRecovery =
    authority.recovery && authorityRecovery === null
      ? authority.recovery
      : null;
  const foreignResolution =
    routeResolution && authorityResolution === null ? routeResolution : null;
  const privilegedWriteBlocked =
    authority.recovery !== null ||
    authority.refreshing ||
    authority.writing ||
    privilegedWriteCount > 0;
  const grantAdminFeedback =
    authorityResolution?.outcome === "confirmed" && authorityResolutionRecovery
      ? `${authorityResolutionRecovery.email} was added to admins@badmintonwarriors.com. They need one fresh platform sign-in if they are already signed in.`
      : null;

  useEffect(() => {
    onGrantFeedbackChange?.(grantAdminFeedback);
  }, [grantAdminFeedback, onGrantFeedbackChange]);

  const displayedGrantError = authorityRecovery
    ? "Admin access changed, but current user access could not be refreshed. Retry to confirm the latest access."
    : authorityResolution?.outcome === "identity-mismatch"
      ? "The grant response did not match the requested user."
      : authorityResolution?.outcome === "diverged"
        ? "The latest user access does not confirm this grant."
        : grantAdminError;
  const retryableAuthority =
    authorityRecovery ??
    (authorityResolution?.outcome === "diverged" && authorityResolutionRecovery
      ? authorityResolutionRecovery
      : (foreignRecovery ??
        (foreignResolution?.outcome === "diverged"
          ? foreignResolution.recovery
          : null)));
  const grantDialogOpen = showGrantAdminDialog || authorityRecovery !== null;

  const grantAdminMutation = useMutation({
    mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
    mutationFn: async ({
      email,
      lease,
    }: {
      email: string;
      lease: PlatformAdminAccessWriteLease;
    }) => {
      const result = await invitePlatformAdmin(email);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      const recovery = Object.freeze({
        email,
        kind: "invite",
        surface: "user",
        userId: user.uid,
      }) satisfies PlatformAdminAccessRecovery;
      const responseMatches =
        result.uid === user.uid &&
        normalizePlatformAdminEmail(result.email) ===
          normalizePlatformAdminEmail(email);
      const evidence = await refreshUserAdminAuthority(
        queryClient,
        user.uid,
        "reconcile",
        () => authority.isWriteOperationCurrent(lease),
      );
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return { evidence, recovery, responseMatches };
    },
    retry: false,
  });

  function closeGrantDialog() {
    if (grantAdminMutation.isPending || privilegedWriteBlocked) return;
    setShowGrantAdminDialog(false);
    setGrantAdminError(null);
  }

  async function submitGrant() {
    if (
      !user.email ||
      queryClient.isMutating({
        exact: true,
        mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
      }) > 0 ||
      !authority.tryStartWrite()
    ) {
      return;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) {
      authority.finishWrite();
      return;
    }
    setGrantAdminError(null);
    try {
      const result = await grantAdminMutation.mutateAsync({
        email: user.email,
        lease,
      });
      if (!result || !authority.isWriteOperationCurrent(lease)) return;
      const { evidence, recovery, responseMatches } = result;
      if (!responseMatches) {
        authority.recordResolution(recovery, "identity-mismatch");
        setShowGrantAdminDialog(true);
        return;
      }
      const outcome = userAdminAuthorityOutcome(
        queryClient,
        recovery,
        evidence,
      );
      if (outcome === "uncertain") {
        authority.recordRecovery(recovery);
      } else {
        authority.recordResolution(recovery, outcome);
        setShowGrantAdminDialog(outcome === "diverged");
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      setGrantAdminError(describeAdminGrantError(error));
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function retryAuthorityReads() {
    if (!retryableAuthority || privilegedWriteCount > 0) return;
    const recovery = retryableAuthority;
    if (!authority.tryStartRefresh(recovery)) return;
    const appliesToCurrentUser =
      recovery.surface === "user" && recovery.userId === user.uid;
    const outcome =
      recovery.surface === "user"
        ? userAdminAuthorityOutcome(
            queryClient,
            recovery,
            await refreshUserAdminAuthority(
              queryClient,
              recovery.userId,
              "retry",
            ),
          )
        : adminRosterAuthorityOutcome(
            queryClient,
            recovery,
            await refreshAdminRosterAuthority(queryClient, "retry"),
          );
    if (outcome === "uncertain") {
      authority.finishRefresh(recovery);
      return;
    }
    if (authority.recovery === recovery) {
      authority.resolveRecovery(recovery, outcome);
    } else {
      authority.recordResolution(recovery, outcome);
    }
    if (appliesToCurrentUser) {
      setShowGrantAdminDialog(outcome === "diverged");
    }
  }

  const isPlatformAdmin = user.roles.includes("platform_admin");
  const isInternalEmployee = isInternalPlatformEmail(user.email);
  const canGrantPlatformAdmin = Boolean(
    user.email && isInternalEmployee && !isPlatformAdmin,
  );

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
        Platform admin control
      </Typography>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Box>
          <Typography color="text.secondary" variant="overline">
            Access state
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isPlatformAdmin ? "Already a platform admin" : "Not yet promoted"}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography color="text.secondary" variant="overline">
            Google identity
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user.email ?? "No Google email identity on file"}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {!user.email
              ? "This account cannot be promoted until it has an internal employee email."
              : !isInternalEmployee
                ? "This identity is not an internal @badmintonwarriors.com employee account, so it should never receive platform admin access."
                : isPlatformAdmin
                  ? "This account is already in the platform admin roster."
                  : "Adding this account to admins@badmintonwarriors.com enables platform access on the next sign-in."}
          </Typography>
        </Box>

        <Box
          role={onGrantFeedbackChange ? undefined : "status"}
          aria-live={onGrantFeedbackChange ? undefined : "polite"}
          aria-atomic="true"
          aria-hidden={onGrantFeedbackChange ? true : undefined}
          sx={
            grantAdminFeedback
              ? {
                  borderLeft: 3,
                  borderColor: "success.main",
                  px: 2,
                  py: 1,
                }
              : {
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  p: 0,
                  m: -1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }
          }
        >
          {grantAdminFeedback ? (
            <Stack spacing={0.5}>
              <Typography
                color="success.main"
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                Access granted
              </Typography>
              <Typography variant="body2">{grantAdminFeedback}</Typography>
            </Stack>
          ) : null}
        </Box>

        {canGrantPlatformAdmin ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            fullWidth
            disabled={privilegedWriteBlocked}
            onClick={() => {
              setGrantAdminError(null);
              setShowGrantAdminDialog(true);
            }}
          >
            Grant platform admin
          </Button>
        ) : (
          <Box>
            <Typography color="text.secondary" variant="overline">
              Grant eligibility
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {!user.email
                ? "An internal employee email is required before this identity can be considered for platform access."
                : !isInternalEmployee
                  ? "Only internal @badmintonwarriors.com employees can receive platform admin access."
                  : "No grant action is needed because this account already has platform access."}
            </Typography>
          </Box>
        )}

        {foreignRecovery || foreignResolution?.outcome === "diverged" ? (
          <Alert
            severity="warning"
            role="alert"
            action={
              retryableAuthority ? (
                <Button
                  type="button"
                  color="inherit"
                  size="small"
                  disabled={authority.refreshing}
                  onClick={() => void retryAuthorityReads()}
                >
                  {authority.refreshing ? "Retrying..." : "Retry"}
                </Button>
              ) : undefined
            }
          >
            {foreignResolution?.outcome === "diverged"
              ? "The latest admin access does not match the requested change."
              : "Another admin-access change needs verification. Retry to finish checking it."}
          </Alert>
        ) : null}
      </Stack>

      <Dialog
        open={grantDialogOpen}
        onClose={closeGrantDialog}
        closeAfterTransition={false}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Grant Platform Admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography color="text.secondary" variant="overline">
                Google account
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user.email ?? "No email available"}
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                The user needs one fresh platform sign-in after this change if
                they are already logged in.
              </Typography>
            </Box>

            {displayedGrantError ? (
              <Alert
                severity="error"
                role="alert"
                action={
                  retryableAuthority ? (
                    <Button
                      type="button"
                      color="inherit"
                      size="small"
                      disabled={authority.refreshing}
                      onClick={() => void retryAuthorityReads()}
                    >
                      {authority.refreshing ? "Retrying..." : "Retry"}
                    </Button>
                  ) : undefined
                }
              >
                {displayedGrantError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            size="small"
            disabled={privilegedWriteBlocked}
            onClick={closeGrantDialog}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            disabled={privilegedWriteBlocked || !user.email}
            onClick={() => void submitGrant()}
          >
            {grantAdminMutation.isPending
              ? "Granting access..."
              : "Grant access"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
